import { Link } from "wouter";
import { Flag, Hourglass, Lock, MessageCircle, Sparkles, Trash2 } from "lucide-react";
import type { ClubAPI } from "@shared/types/api";
import { youTubeEmbedUrl } from "@shared/youtube";
import Markdown from "@/components/Markdown";
import { useConfirm } from "@/components/ConfirmModal";
import { useToast } from "@/components/ux/Toast";
import { useT } from "@/lib/i18n";
import { ClubAuthorAvatar } from "./ClubAuthorAvatar";
import { postHeadline, threadExcerpt } from "./postText";
import { useDeleteClubPost, useReportClubPost } from "@/lib/api";

/** Inline notice shown in place of a gated control. */
export function GateNotice({ message, actionLabel, onAction }: { message: string; actionLabel?: string; onAction?: () => void }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-dashed border-neutral-300 dark:border-neutral-700 px-4 py-3 text-sm">
      <span className="flex items-center gap-2 text-dark-muted">
        <Lock className="w-4 h-4 flex-shrink-0" /> {message}
      </span>
      {actionLabel && onAction && (
        <button onClick={onAction} className="btn btn-primary btn-sm flex-shrink-0">
          {actionLabel}
        </button>
      )}
    </div>
  );
}

/** 16:9 YouTube player for admin posts; loads lazily and sets no cookies until played. */
export function YouTubeEmbed({ videoId, title }: { videoId: string; title?: string | null }) {
  return (
    <div className="relative w-full aspect-video overflow-hidden rounded-xl bg-black">
      <iframe
        src={youTubeEmbedUrl(videoId)}
        title={title ?? "YouTube video"}
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        referrerPolicy="strict-origin-when-cross-origin"
        allowFullScreen
        className="absolute inset-0 w-full h-full border-0"
      />
    </div>
  );
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export function ClubPostCard({
  post,
  slug,
  linkToThread = true,
}: {
  post: ClubAPI.ClubPost;
  slug: string;
  linkToThread?: boolean;
}) {
  const { t } = useT();
  const toast = useToast();
  const { confirm } = useConfirm();
  const deletePost = useDeleteClubPost(slug);
  const reportPost = useReportClubPost(slug);

  const authorName = post.isAdminPost ? t("clubs.outclose") : post.author?.firstName ?? t("clubs.deletedUser");

  const handleReport = async () => {
    const ok = await confirm({ message: t("clubs.reportPrompt"), confirmText: t("clubs.report") });
    if (!ok) return;
    reportPost.mutate(
      { postId: post.id, reason: "inappropriate" },
      { onSuccess: () => toast.success(t("clubs.reported")), onError: (e) => toast.error(e.message) },
    );
  };

  const handleDelete = () =>
    deletePost.mutate(post.id, {
      onSuccess: () => toast.success(t("clubs.deleted")),
      onError: (e) => toast.error(e.message),
    });

  return (
    <article className="card">
      <header className="flex items-center gap-2 mb-2 text-sm">
        <ClubAuthorAvatar post={post} />
        <span className="font-semibold">{authorName}</span>
        <span className="text-dark-muted">· {formatTime(post.createdAt)}</span>
      </header>
      {post.title && <h3 className="font-bold text-lg leading-snug mb-1">{post.title}</h3>}
      <Markdown className="break-words">{post.body}</Markdown>
      {post.youtubeVideoId && (
        <div className="mt-3">
          <YouTubeEmbed videoId={post.youtubeVideoId} title={post.title} />
        </div>
      )}
      <footer className="flex items-center gap-4 mt-3 text-xs text-dark-muted">
        {linkToThread && !post.parentId && (
          <Link href={`/clubs/${slug}/posts/${post.id}`} className="inline-flex items-center gap-1 hover:text-primary-500">
            <MessageCircle className="w-3.5 h-3.5" /> {t("clubs.replies", { count: post.replyCount })}
          </Link>
        )}
        <span className="ml-auto flex items-center gap-3">
          {post.isOwn ? (
            <button onClick={handleDelete} className="inline-flex items-center gap-1 hover:text-red-500">
              <Trash2 className="w-3.5 h-3.5" /> {t("clubs.delete")}
            </button>
          ) : (
            !post.isAdminPost && (
              <button onClick={handleReport} className="inline-flex items-center gap-1 hover:text-red-500">
                <Flag className="w-3.5 h-3.5" /> {t("clubs.report")}
              </button>
            )
          )}
        </span>
      </footer>
    </article>
  );
}

/**
 * A board card on the club page: author, headline, excerpt, reply count — opens
 * the thread. A post's video plays right on the card (outside the link).
 */
export function BoardCard({ post, slug }: { post: ClubAPI.ClubPost; slug: string }) {
  const { t } = useT();
  const authorName = post.isAdminPost ? t("clubs.outclose") : post.author?.firstName ?? t("clubs.deletedUser");
  const title = postHeadline(post);
  const excerpt = post.title ? threadExcerpt(`\n${post.body}`) : threadExcerpt(post.body);
  const href = `/clubs/${slug}/posts/${post.id}`;
  return (
    <div className="card space-y-2 hover:ring-2 hover:ring-primary-500/40 transition">
      <Link href={href} className="block space-y-2">
        <div className="flex items-center gap-2 text-xs">
          <ClubAuthorAvatar post={post} size="xs" />
          <span className="font-semibold text-sm">{authorName}</span>
          <span className="text-dark-muted">· {formatTime(post.createdAt)}</span>
        </div>
        <div className="font-bold leading-snug">{title}</div>
        {excerpt && <p className="text-sm text-dark-muted line-clamp-3">{excerpt}</p>}
      </Link>
      {post.youtubeVideoId && <YouTubeEmbed videoId={post.youtubeVideoId} title={post.title} />}
      <Link href={href} className="flex items-center gap-1 text-xs text-dark-muted pt-1">
        <MessageCircle className="w-3.5 h-3.5" /> {post.replyCount}
      </Link>
    </div>
  );
}

/**
 * Small chip shown once the viewer's city has enough members for a meetup.
 * Deliberately no counts: members only learn that something is being planned.
 */
export function MeetupPlannedChip({ city }: { city: ClubAPI.ViewerCity | null }) {
  const { t } = useT();
  if (!city?.meetupReady) return null;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium bg-primary-500/15 text-primary-500">
      <Sparkles className="w-3.5 h-3.5" /> {t("clubs.meetupPlanned", { city: city.cityName ?? city.cityId })}
    </span>
  );
}

/** Label for an unopened (teased) club. */
export function ComingSoonBadge() {
  const { t } = useT();
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium bg-primary-500/15 text-primary-500">
      <Hourglass className="w-3.5 h-3.5" /> {t("clubs.comingSoon")}
    </span>
  );
}
